import { useFragment } from 'react-relay/hooks'

import { graphql } from 'relay-runtime'
import { jobApplication_useIsUnsentJobApplication$key } from '__generated__/jobApplication_useIsUnsentJobApplication.graphql'
import {
  JobApplicationFilteredOutReasonEnum,
  jobApplication_useFilteredOutReasons$key,
} from '__generated__/jobApplication_useFilteredOutReasons.graphql'
import { useMemo } from 'react'
import { Nullable } from 'types'

export function useIsUnsentJobApplication(jobApplicationFragmentRef: Nullable<jobApplication_useIsUnsentJobApplication$key>) {
  const jobApplication = useFragment(graphql`
    fragment jobApplication_useIsUnsentJobApplication on JobApplication {
      status

      contract {
        id
      }
    }
  `, jobApplicationFragmentRef)
  if (jobApplication?.contract?.id) return false
  if (!jobApplication) return null

  return ['potential', 'job_viewed', 'job_application_draft', 'job_application_invited'].includes(jobApplication?.status)
}

// TODO: When we did DEV-5264 we didn't clean up the filter arguments properly so a lot of these don't make sense for job applications
// Fix that on the backend too by deleting useless filter arguments from the API.
const humanizedFilteredOutReasons: Record<JobApplicationFilteredOutReasonEnum, string> = {
  stage: 'Stage',
  category: 'Category',
  noCategory: 'Category',
  contractType: 'Contract Type',
  user: 'Hiring Panel',
  firmId: 'Company',
  clientId: 'Manager',
  hiringManagerId: 'Hiring Manager',
  freelancerId: 'Applicant/Candidate',
  freelancerRawIdOrSlug: 'Applicant/Candidate',
  clientRawId: 'Client',
  jobId: 'Job',
  tagsIds: 'Tags',
  skillsIds: 'Skills',
  status: 'Status',
  statuses: 'Status',
  name: 'Name',
  withJobScreening: 'Job Screening',
  withoutJobScreening: 'Job Screening',
  locationTypeFilter: 'Location',
  timezoneFilter: 'Timezone',
  countries: 'Countries',
  positionTypes: 'Position Types',
  maxClientRate: 'Max Client Rate',
  canWorkInTheUs: 'Can Work in the US',
  verifiedOnly: 'Verified Only',
  excludeHidden: 'Hidden',
  adminMode: 'Admin Mode',
  availableOnly: 'Available Only',
  managedTeams: 'Managed Teams',
  showNotified: 'Show Notified',
  showNotNotified: 'Show Not Notified',
  missingProjectSubmission: 'Missing Code Test',
  hasProjectSubmission: 'Has Code Test',
  hasAnswers: 'All Questions Answered',
  missingAnswers: 'Missing Answers',
  hasVideoIntroduction: 'Has Video Introduction',
  missingVideoIntroduction: 'Missing Video Introduction',
  rating: 'Rating',
  contractStatus: 'Status',
  jobApplicationStatus: 'Status',
  feedbackCountBelow: 'Feedback Count',
  feedbackCountEqualOrAbove: 'Feedback Count',
  netFeedback: 'Net Feedback',
  interviewStatus: 'Interview Status',
  applicantSource: 'Applicant Source',
  evaluated: 'Evaluated',
  needsEvaluation: 'Needs Evaluation',
  experience: 'Experience',
  skills: 'Skills',
  specializations: 'Specializations',
  jobsIds: 'Jobs',
  university: 'University',
  maxUniversityRank: 'Max University Rank',
  bookmarkedByUserId: 'Bookmark',
  bookmarked: 'Bookmark',
  company: 'Company',
  fortune500: 'Fortune 500',
  sortBy: 'Sort By',
  order: 'Order',
}

export function useFilteredOutReasons(jobApplicationFragmentRef: jobApplication_useFilteredOutReasons$key) {
  const jobApplication = useFragment(graphql`
    fragment jobApplication_useFilteredOutReasons on JobApplication
    @argumentDefinitions(
      filters: { type: "JobApplicationsFilters" }
    )
    {
      filteredOutReasons(filters: $filters)
    }
  `, jobApplicationFragmentRef)

  return useMemo(() => (
    jobApplication?.filteredOutReasons?.map(reason => humanizedFilteredOutReasons[reason] || reason) || []
  ), [jobApplication])
}
